import styled, { css } from 'styled-components';
import { IconHalfSun, IconMoon, IconSun } from '@/app/common/icons/libraryIcons';
import {
  BoundHeader,
  Filter,
  FilterContainer,
  FilterHeader,
  FilterPrice,
  FilterTitle,
  type TimeSlotsFilterChange,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { useTranslation } from '@/app/components/translations/Translation';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { FilterLoadingLayer } from './ChangeTransportFiltersLoading';
import type { ClickParams } from '../../search/Curiosity/Analytics/utils';
import { useTransportPageFilterTracking } from './hooks/useTransportPageFilterTracking';

const TimeSlotOptions = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const TimeSlotTitle = styled.span``;

const TimeSlotIcon = styled.div`
  border: 1px solid ${(p) => p.theme.commons.colors.gray100};
  border-radius: 100%;
  width: 3rem;
  height: 3rem;
  display: flex;
  padding: 0.5rem;

  & [data-library-icon] {
    width: 100%;
    height: 100%;
  }
`;

const TimeSlotRange = styled.div`
  color: ${({ theme }) => theme.commons.colors.gray400};
`;

export type ChangeTransportTimeSlotFilterProps = {
  outboundTimeSlots?: TimeSlotFilter[];
  inboundTimeSlots?: TimeSlotFilter[];
  onChange: (filterChange: TimeSlotsFilterChange) => void;
};

export function getTimeSlotFilterChangeData(
  type: BoundType,
  bound: TimeSlotFilter[],
  newFilter: TimeSlotFilterId,
  sendCuriosityClickEvent: (
    eventData: ClickParams,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void,
  sendStratrackFlightPageFilterEvent: (payload: StartrackFilterPayload) => void
): TimeSlotsFilterChange {
  const newStateMapper = (val: TimeSlotFilter) => (val.id === newFilter ? { ...val, selected: !val.selected } : val);
  const origin = type === 'outbound' ? 'DEP_TIME_OUTBOUND_FILTER' : 'DEP_TIME_RETURN_FILTER';
  sendStratrackFlightPageFilterEvent({
    filterName: origin,
    value: newFilter,
    deselectFlag: !!bound.find((slot) => slot.id === newFilter)?.selected || false,
  });
  sendCuriosityClickEvent({
    event_name: `click_on_${type}_time_slot_filters`,
    event_context: 'change_transport_page',
    event_value: newFilter,
  });
  return {
    newState: {
      ...(type === 'outbound'
        ? { outboundDepartureTimeSlots: bound.map(newStateMapper) }
        : { inboundDepartureTimeSlots: bound.map(newStateMapper) }),
    },
    event: { type: 'FILTER', origin },
  };
}

const slotTimesSort: TimeSlotFilterId[] = ['MORNING', 'AFTERNOON', 'EVENING', 'NIGHT'];

export function sortTimeSlots(slots: TimeSlotFilter[]) {
  return slots
    .map((timeSlot) => ({ ...timeSlot, index: slotTimesSort.indexOf(timeSlot.id) }))
    .sort((a, b) => a.index - b.index);
}

export default function ChangeTransportTimeSlotFilter(props: ChangeTransportTimeSlotFilterProps) {
  const { outboundTimeSlots, inboundTimeSlots, onChange } = props;
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  return outboundTimeSlots || inboundTimeSlots ? (
    <Filter>
      <FilterHeader>
        <FilterTitle>
          {isTrain
            ? t('vacation.trainavailability.filters.traveltimes')
            : t('vacation.flightavailability.filters.traveltimes')}
        </FilterTitle>
      </FilterHeader>
      {outboundTimeSlots && (
        <FilterContainer>
          <BoundHeader type="outbound" />
          <TimeSlotOptions>
            {sortTimeSlots(outboundTimeSlots).map((timeSlot) => (
              <TimeSlotOption
                key={timeSlot.id}
                {...timeSlot}
                isTrain={isTrain}
                onTimeSlotFilterChange={(value) =>
                  onChange(
                    getTimeSlotFilterChangeData(
                      'outbound',
                      outboundTimeSlots,
                      value,
                      sendCuriosityClickEvent,
                      sendStratrackFlightPageFilterEvent
                    )
                  )
                }
              />
            ))}
            <FilterLoadingLayer origin="DEP_TIME_OUTBOUND_FILTER" />
          </TimeSlotOptions>
        </FilterContainer>
      )}
      {inboundTimeSlots && (
        <FilterContainer>
          <BoundHeader type="inbound" />
          <TimeSlotOptions>
            {sortTimeSlots(inboundTimeSlots).map((timeSlot) => (
              <TimeSlotOption
                key={timeSlot.id}
                {...timeSlot}
                isTrain={isTrain}
                onTimeSlotFilterChange={(value) =>
                  onChange(
                    getTimeSlotFilterChangeData(
                      'inbound',
                      inboundTimeSlots,
                      value,
                      sendCuriosityClickEvent,
                      sendStratrackFlightPageFilterEvent
                    )
                  )
                }
              />
            ))}
            <FilterLoadingLayer origin="DEP_TIME_RETURN_FILTER" />
          </TimeSlotOptions>
        </FilterContainer>
      )}
    </Filter>
  ) : null;
}

type TimeSlotOptionProps = {
  onTimeSlotFilterChange: (id: TimeSlotFilterId) => void;
  isTrain: boolean;
} & TimeSlotFilter;

function TimeSlotOption(props: TimeSlotOptionProps) {
  const { minPrice, id, selected, enabled, isTrain, onTimeSlotFilterChange } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const hidePriceInFilters = localization?.configValuesApp?.hidePriceInFilters;

  return (
    <div
      role="button"
      tabIndex={0}
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: ${(p) => p.theme.commons.font.sizes.xsmall};
        cursor: ${enabled ? 'pointer' : 'initial'};

        ${
          selected
            ? css`
                & {
                  ${TimeSlotIcon} {
                    background-color: ${({ theme }) => theme.commons.colors.primary50};
                  }

                  ${TimeSlotTitle} {
                    color: ${({ theme }) => theme.commons.colors.primary};
                  }

                  ${TimeSlotRange} {
                    color: ${({ theme }) => theme.commons.colors.primary400};
                  }
                }
              `
            : ''
        }

        ${
          enabled
            ? css`
                &:hover {
                  ${TimeSlotIcon} {
                    background-color: ${({ theme }) => theme.commons.colors.primary50};
                  }

                  ${TimeSlotTitle} {
                    color: ${({ theme }) => theme.commons.colors.primary};
                  }

                  ${TimeSlotRange} {
                    color: ${({ theme }) => theme.commons.colors.primary400};
                  }
                }
              `
            : css`
                color: ${(p) => p.theme.commons.colors.gray400};

                & [data-library-icon] {
                  color: ${(p) => p.theme.commons.colors.gray100};
                }
              `
        }
      `}
      onClick={enabled ? () => onTimeSlotFilterChange(id) : undefined}
      onKeyUp={enabled ? keyUpHandler(() => onTimeSlotFilterChange(id)) : undefined}
    >
      {id === 'NIGHT' ? (
        <>
          <TimeSlotIcon>
            <IconMoon />
          </TimeSlotIcon>
          <TimeSlotTitle>
            {isTrain
              ? t('vacation.trainavailability.filters.timeslot.night')
              : t('vacation.flightavailability.filters.timeslot.night')}
          </TimeSlotTitle>
        </>
      ) : id === 'MORNING' ? (
        <>
          <TimeSlotIcon>
            <IconHalfSun />
          </TimeSlotIcon>
          <TimeSlotTitle>
            {isTrain
              ? t('vacation.trainavailability.filters.timeslot.morning')
              : t('vacation.flightavailability.filters.timeslot.morning')}
          </TimeSlotTitle>
        </>
      ) : id === 'AFTERNOON' ? (
        <>
          <TimeSlotIcon>
            <IconSun />
          </TimeSlotIcon>
          <TimeSlotTitle>
            {isTrain
              ? t('vacation.trainavailability.filters.timeslot.afternoon')
              : t('vacation.flightavailability.filters.timeslot.afternoon')}
          </TimeSlotTitle>
        </>
      ) : id === 'EVENING' ? (
        <>
          <TimeSlotIcon>
            <IconHalfSun />
          </TimeSlotIcon>
          <TimeSlotTitle>
            {isTrain
              ? t('vacation.trainavailability.filters.timeslot.evening')
              : t('vacation.flightavailability.filters.timeslot.evening')}
          </TimeSlotTitle>
        </>
      ) : null}
      {!hidePriceInFilters && <FilterPrice price={enabled ? minPrice : 0} />}
    </div>
  );
}
